let inErrorHandlingMode = false;

import { MiaPlaza } from "@/Reinforced.Typings";
import type { ComponentPublicInstance } from "vue";
import type { StackFrame } from "stacktrace-js";

interface LogErrorOptions {
	captureSyntheticStack?: boolean
}

// Writes to the Log on the *server*.
export default class Log {
	private static readonly MAX_DESCRIPTION_LENGTH = 400;
	private static readonly MAX_REPORTS_PER_WINDOW = 2;
	private static readonly RATE_LIMIT_WINDOW_MS = 30_000;
	private static rateLimitEnabled = true;
	private static reportTimestamps: number[] = [];

	// How many ancestors of a failing component to name before the trace is more noise than help.
	private static readonly MAX_COMPONENT_DEPTH = 8;

	private static describeError(error: unknown): string {
		const description = Log.describeUncapped(error);

		return description.length > Log.MAX_DESCRIPTION_LENGTH
			? `${description.slice(0, Log.MAX_DESCRIPTION_LENGTH - 1)}…`
			: description;
	}

	private static describeUncapped(error: unknown): string {
		if (error instanceof Error) {
			if (!error.message) {
				return error.name || "Error";
			}

			// The type of an error is frequently the most important part, for example "TypeError"
			// or "RangeError". But the type of a basic Error gives no data, thus remove it.
			const type = error.name && error.name !== "Error" ? `${error.name}: ` : "";
			return `${type}${error.message}`;
		}

		if (error instanceof ErrorEvent) {
			if (error.message) {
				return error.message;
			}
			if (error.error != null) {
				return Log.describeUncapped(error.error);
			}
			// Carries nothing of its own; fall through and describe it as the event it is.
		}

		if (error instanceof Event) {
			const target = error.target instanceof Element
				? ` target=${Log.describeElement(error.target)}`
				: "";
			return `${error.constructor.name} (type="${error.type}"${target})`;
		}

		if (typeof error === "string") {
			return error;
		}

		const serialized = Log.serialize(error);
		return serialized !== undefined && serialized !== "{}" ? serialized : Log.describeOpaque(error);
	}

	// Last resort for a value that carries no data JSON can express. Its own string representation
	// is preferable when it has one, but the default "[object Object]" is exactly the description
	// this class exists to avoid, and a null-prototype value cannot be converted to a string at
	// all. The type name is all that is left.
	private static describeOpaque(value: unknown): string {
		try {
			const asString = String(value);
			if (asString !== "[object Object]") {
				return asString;
			}
		}
		catch {
			// Not convertible to a primitive; fall through to the type name.
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- a null-prototype value has no constructor
		const name = (value as { constructor?: { name?: string } }).constructor?.name;
		return name ? `${name} {}` : "{}";
	}

	// Identify the element the way it appears in the markup, so it can be found again in the DOM.
	// `getAttribute` rather than `id`/`className`: the latter is not a string on SVG elements.
	private static describeElement(element: Element): string {
		const attributes = ["id", "class"]
			.map((name) => {
				const value = element.getAttribute(name);
				return value ? ` ${name}="${value}"` : "";
			})
			.join("");

		return `<${element.tagName.toLowerCase()}${attributes}>`;
	}

	// Self-referencing values would make a plain JSON.stringify throw, leaving nothing but a
	// useless "[object Object]", so replace the repeated occurrence instead of giving up.
	private static serialize(value: unknown): string | undefined {
		const ancestors: unknown[] = [];

		try {
			return JSON.stringify(value, function (this: unknown, _key, entry: unknown) {
				if (typeof entry !== "object" || entry === null) {
					return entry;
				}

				while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
					ancestors.pop();
				}

				if (ancestors.includes(entry)) {
					return "[circular]";
				}
				ancestors.push(entry);

				return entry;
			});
		}
		catch {
			// Anything else JSON cannot handle, such as a BigInt or a getter that throws.
			return undefined;
		}
	}

	// Unwrap ErrorEvent because only its underlying Error carries the original stack.
	// An error without a stack cannot be parsed by stacktrace-js - `fromError()` would reject and
	// we would report nothing at all - so treat it as a non-Error and fall back to a synthetic stack.
	private static extractError(value: unknown): Error | undefined {
		if (value instanceof Error && value.stack) {
			return value;
		}

		if (value instanceof ErrorEvent && value.error instanceof Error && value.error.stack) {
			return value.error;
		}

		return undefined;
	}

	/**
	 * Enables or disables the client-side report limit. This is intended for diagnostic pages that
	 * need to exercise several logging paths in one session. Changing the setting starts a fresh
	 * window so diagnostic traffic cannot consume the normal application's allowance.
	 */
	public static setRateLimitEnabled(enabled: boolean): void {
		Log.rateLimitEnabled = enabled;
		Log.reportTimestamps = [];
	}

	private static reserveReportSlot(): boolean {
		if (!Log.rateLimitEnabled) {
			return true;
		}

		const now = Date.now();
		const windowStart = now - Log.RATE_LIMIT_WINDOW_MS;
		Log.reportTimestamps = Log.reportTimestamps.filter((timestamp) => timestamp > windowStart);
		if (Log.reportTimestamps.length >= Log.MAX_REPORTS_PER_WINDOW) {
			return false;
		}

		Log.reportTimestamps.push(now);
		return true;
	}

	// Log an error to the server.
	public static async error(message: unknown, originalError?: unknown, options: LogErrorOptions = {}): Promise<void> {
		// Capture synthetic stacks before the dynamic import; an await loses the caller chain.
		const realError = Log.extractError(originalError) ?? Log.extractError(message);
		const error = realError ?? (options.captureSyntheticStack === false ? undefined : new Error());
		try {
			// eslint-disable-next-line no-console
			console.error(message, originalError ?? "");
			if (!Log.reserveReportSlot()) {
				return;
			}

			const StackTrace = await import("stacktrace-js");
			const frames = error ? await StackTrace.fromError(error).catch(() => []) : [];
			let stack = frames;
			if (!realError) {
				// Synthetic stacks start here, so remove logging frames from the reported trace.
				// If no frame names a file other than the first one's, source maps did not resolve
				// and that name is the bundle rather than this module, so stripping would discard
				// the whole stack. Anonymous and native frames carry no file name at all and prove
				// nothing either way, so only named frames count as evidence of resolution.
				const ownFile = frames[0]?.fileName;
				const hasFramesFromAnotherFile = frames.some(
					(frame) => frame.fileName !== undefined && frame.fileName !== ownFile,
				);
				if (ownFile !== undefined && hasFramesFromAnotherFile) {
					let i = 0;
					while (i < frames.length && frames[i].fileName === ownFile) {
						i++;
					}
					stack = frames.slice(i);
				}
			}
			await StackTrace.report(
				stack,
				MiaPlaza.LO.JavaScript.Log.ErrorLoggingUrl,
				Log.describeError(message),
			);
		}
		catch (err) {
			// eslint-disable-next-line no-console
			console.error(`Could not report error ${String(message)} to server due to ${String(err)}.`);
		}
	}

	// `error` is whatever was thrown; browsers do not guarantee it is an Error, or that they hand
	// one over at all.
	public static async onerror(
		message: string,
		url: string,
		line?: number,
		column?: number,
		error?: unknown,
	): Promise<void> {
		// eslint-disable-next-line no-console
		console.error(error ?? message);

		// The general error message 'Script error.' ('Script error'
		// for IE) may be sent when an error was thrown in a script from
		// a different origin.
		// This is a safety feature inside some browsers to prevent
		// information leaks
		// (https://en.wikipedia.org/wiki/Same-origin_policy).
		// As the meta information for such errors is modified in a
		// way that makes it useless (line number 0, script '', ...), we
		// can safely throw it away.
		// If we ever host some scripts on different domains
		// ourselves, we should make sure that they can get through.
		// For more information, see
		// https://danlimerick.wordpress.com/2014/01/18/how-to-catch-javascript-errors-with-window-onerror-even-on-chrome-and-firefox/.
		if (message === "Script error." || message === "Script error") {
			return;
		}

		// Sentry was being overflown with logs from a malicious ad blocker
		// extension, upon further investigation, every log contained this number,
		// the next statement is to ensure that those logs are dropped, and not sent
		// to the backend. For more information, see
		// https://dev.miaplaza.com/miaplaza/website/-/issues/15816
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the thrown value can be any shape
		const thrownMessage: unknown = (error as { message?: unknown } | null | undefined)?.message;
		const adBlockerSpam = "198230182308109283091823098102938908128390";
		if (message.includes(adBlockerSpam)
			|| (typeof thrownMessage === "string" && thrownMessage.includes(adBlockerSpam))) {
			return;
		}

		if (inErrorHandlingMode) {
			// eslint-disable-next-line no-console
			console.error(`Failed to log error as logging called itself recursively: ${message}`);
			return;
		}
		if (!Log.reserveReportSlot()) {
			return;
		}

		inErrorHandlingMode = true;
		// parse sourcemaps with stacktrace-js asynchronously to get readable stacktraces
		try {
			const StackTrace = await import("stacktrace-js");
			// `error` is whatever was thrown, so it is not necessarily an Error with a parsable
			// stack. Rather than let stacktrace-js reject and report nothing at all, fall back to
			// the single location the browser handed us alongside the message — and to no location
			// at all for the errors that arrive without one, as "at :0:0" says nothing.
			const position = line
				? { lineNumber: line, ...column ? { columnNumber: column } : {} }
				: {};
			const fallback = url
				// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- report serializes frame data, not StackFrame methods
				? [{ fileName: url, ...position } as StackFrame]
				: [];
			const realError = Log.extractError(error);
			const stack = realError
				? await StackTrace.fromError(realError).catch(() => fallback)
				: fallback;
			// The browser builds this message from the thrown value, so it is as unbounded as that
			// value's own message and needs the same cap as every other description.
			await StackTrace.report(stack, MiaPlaza.LO.JavaScript.Log.ErrorLoggingUrl, Log.describeError(message));
		}
		catch (err) {
			// eslint-disable-next-line no-console, @typescript-eslint/restrict-template-expressions
			console.error(`Failed to log error: ${err}`);
		}
		finally {
			inErrorHandlingMode = false;
		}
	}

	/**
	 * Handles window.onerror events
	 */
	public static onWindowError(
		event: Event | string,
		source?: string,
		lineno?: number,
		colno?: number,
		error?: unknown,
	): boolean | void {
		let message: string;

		if (typeof event === "string") {
			message = event;
		}
		else if (event instanceof ErrorEvent) {
			message = event.message;
		}
		else {
			message = event.type;
		}

		if (error !== undefined && error !== null && !(error instanceof Error)) {
			message = `Uncaught ${Log.describeError(error)}`;
		}

		void Log.onerror(message, source ?? "", lineno, colno, error);
	}

	/**
	 * Handles unhandled promise rejections
	 */
	public static onUnhandledRejection(event: PromiseRejectionEvent): void {
		// This runs from the microtask queue, so the code that created the rejected promise is no
		// longer on the stack: a synthetic capture here can only ever produce this module's own
		// frames. A rejected Error still carries its origin stack and is reported with it.
		void Log.error(event.reason, undefined, { captureSyntheticStack: false });
	}

	/**
	 * Handles Vue 3 component errors
	 */
	public static onVueError(
		error: unknown,
		instance: ComponentPublicInstance | null,
		info: string,
	): void {
		// The description comes before the trace because the whole line is capped again on its way
		// out: what a long component trace crowds out has to be the ancestry, never the error.
		const message = `Vue error during ${info}: ${Log.describeError(error)} in component ${Log.resolveComponentTrace(instance)}`;

		// A non-Error has no origin stack; capturing here would contain only Vue's forwarding internals.
		void Log.error(message, error, { captureSyntheticStack: false });
	}

	// Registry keys survive minification, unlike component class names in production bundles.
	private static resolveComponentTrace(instance: ComponentPublicInstance | null): string {
		interface InternalInstance {
			type: { __name?: string, name?: string }
			parent: InternalInstance | null
			appContext?: { components?: Record<string, unknown> }
		}

		// `instance.$` returns the internal component instance (see Vue's `publicPropertiesMap`).
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- reading a documented Vue internal
		let current: InternalInstance | null | undefined = (instance as unknown as { $?: InternalInstance } | null)?.$;
		if (!current) {
			return "<anonymous>";
		}

		const registry = current.appContext?.components;

		const nameOf = (type: InternalInstance["type"]): string => {
			if (registry && type) {
				for (const tag in registry) {
					if (registry[tag] === type) {
						return tag;
					}
				}
			}

			return type?.__name || type?.name || "anonymous";
		};

		const trace: string[] = [];
		for (let depth = 0; current && depth < Log.MAX_COMPONENT_DEPTH; depth++) {
			trace.push(`<${nameOf(current.type)}>`);
			current = current.parent;
		}

		if (current) {
			// Mark the cut so a truncated trace cannot be mistaken for one that reached the root.
			trace.push("…");
		}

		return trace.join(" ← ");
	}
}
